"use client"

import { useSyncExternalStore } from "react"

// Module-level TTL response cache for client-side hooks.
//
// Why module-level (not useRef): a useRef cache is per-component-instance.
// When a tabbed view unmounts (e.g. user switches Economics → Products), the
// ref dies and the next remount triggers a full refetch. Module-level state
// survives across mount/unmount within the same SPA session, which is the
// behavior users expect when clicking back to a tab they just visited.
//
// FIFO eviction with a soft cap on entries: prevents unbounded memory growth
// when a user explores many distinct period selections in one session. TTL
// guards against stale data after long idle without forcing a hard refresh.

interface CachedEntry<T> {
  data: T
  expiresAt: number
}

/** Every live cache, so a change that invalidates all cached economics can reach them. */
const registry = new Set<{ clear(): void }>()
let generation = 0
const listeners = new Set<() => void>()

/**
 * Drop every cached economics response — costs are resolved on the server, so a COGS write
 * makes them stale — and bump the generation so mounted views refetch.
 */
export function clearAllResponseCaches(): void {
  for (const cache of registry) cache.clear()
  generation += 1
  for (const notify of listeners) notify()
}

/** Add to a fetch callback's deps: it changes whenever the caches are cleared. */
export function useResponseCacheGeneration(): number {
  return useSyncExternalStore(subscribe, currentGeneration, currentGeneration)
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function currentGeneration(): number {
  return generation
}

export class ResponseCache<T> {
  private readonly map = new Map<string, CachedEntry<T>>()

  constructor(
    private readonly ttlMs: number,
    private readonly maxEntries: number = 50,
  ) {
    registry.add(this)
  }

  /** Returns cached data if present AND not expired. Lazy-evicts expired entries. */
  get(key: string): T | null {
    const entry = this.map.get(key)
    if (!entry) return null
    if (entry.expiresAt < Date.now()) {
      this.map.delete(key)
      return null
    }
    return entry.data
  }

  /** Skipped when the caches were cleared after the request started at `requestGeneration`. */
  set(key: string, data: T, requestGeneration: number): void {
    if (requestGeneration !== generation) return
    // Re-insert to refresh FIFO position when key exists. Otherwise enforce
    // the max-entries cap before insertion.
    if (this.map.has(key)) {
      this.map.delete(key)
    } else if (this.map.size >= this.maxEntries) {
      const oldest = this.map.keys().next().value
      if (oldest !== undefined) this.map.delete(oldest)
    }
    this.map.set(key, { data, expiresAt: Date.now() + this.ttlMs })
  }

  /** Drop a single entry — used by callers that want to force refetch on next call. */
  delete(key: string): void {
    this.map.delete(key)
  }

  /** Drop everything — used on auth changes / explicit logout. */
  clear(): void {
    this.map.clear()
  }
}
